// src/encounter/services/advance-care-models-on-sign.ts
// Signing an encounter moves the patient's care model past a step that was waiting on the visit
// (contract C3: POST /workflows/care-models/enrolments/advance { patientId, encounterId }).
//
// ONLY A SIGN-AND-LOCK (`sign === true && lock === true`) advances the plan (ruling, Samer/Andrew: only
// `lock === true` advances; the sign half was already required and stays). A nurse sign (lock:false), or a
// lock without a sign, sends nothing and does NOT consume the once-guard below, so the doctor's later
// sign-and-lock of the same encounter still sends it.
//
// FIRE-AND-REPORT. The sign is the clinical act; the advance is a consequence of it. So:
//   · it runs only AFTER CreateSignLock succeeded (the caller is `useCreateSignLock`'s onSuccess),
//     never on a failed or in-flight sign;
//   · it is never awaited by the sign — nothing here returns a promise to the caller, so a slow,
//     failing or missing Workflows endpoint cannot delay, fail or undo the sign;
//   · a failure is said in words through the app's toast channel, with a way to try again;
//   · it goes out ONCE per (patient, encounter) per session — the guard is keyed on BOTH ids. The
//     server (Scrips.Workflows C3) is idempotent per (patient, encounter): a repeat answers 200 and
//     moves nothing. So a reload, a second tab or "Try again" is safe server-side; the client guard only
//     saves the round trip. No Idempotency-Key header is sent: the server ignores one. A FAILED attempt
//     releases the claim, so "Try again" — or the next sign-and-lock of the same encounter — can send it
//     again. That re-send is deliberate: the step has not moved.
//   · WHAT WARNS. A 200 whose `failed` is empty is success — including `advanced: []`, which is how the
//     server answers "no active enrolment", "nothing waited on a visit" and "not a provider
//     sign-and-lock". A warning shows only for a non-empty `failed` (an enrolment's resume faulted), a
//     409 (another request is advancing it — busy, retry), any other 4xx/5xx, or a network error.
// A vaccination sign counts: it is sent as a sign-and-lock (`lock: true`, encounter-shell-page.tsx), and
// a vaccination-only visit has no other. Every sign path is covered because the one success point they share is the hook.
//
// Default OFF behind `env.careModelAdvanceEnabled` (VITE_CARE_MODEL_ADVANCE) — see env.ts.
// Nothing is logged: the ids are patient identifiers and the notice needs neither.
import type { QueryClient } from '@tanstack/react-query';
import { env } from '@/config/env';
import { ApiError } from '@/shared/api/api-client';
import { showWarning } from '@/shared/lib/toast-helpers';
import type { SignLockModel } from '@/types/sign-lock-api';
import { advanceCareModels } from './care-model-api';
import { carePlansKey, patientEnrolmentsKey } from './use-care-plans';

export const ADVANCE_FAILED_MESSAGE =
  "The note is signed. The patient's care plan did not move on to its next step.";

/** Some enrolments moved and at least one did not: never say "did not move" when part of it did. */
export const ADVANCE_PARTIAL_MESSAGE =
  "The note is signed. Part of the patient's care plan moved on, but one step could not be completed.";

/** 409: another request holds the enrolment right now. Nothing is known to have failed yet. */
export const ADVANCE_BUSY_MESSAGE =
  "The note is signed. The patient's care plan is being updated by another request. Try again in a moment.";

// Keyed on the QueryClient so the claim lives exactly as long as the app's server-state cache.
const claimed = new WeakMap<QueryClient, Set<string>>();

/** The once-guard key: the (patient, encounter) pair the server is idempotent on. */
const claimKey = (patientId: string, encounterId: string) => JSON.stringify([patientId, encounterId]);

function claim(qc: QueryClient, key: string): boolean {
  let keys = claimed.get(qc);
  if (!keys) claimed.set(qc, (keys = new Set()));
  if (keys.has(key)) return false;
  keys.add(key);
  return true;
}

async function send(qc: QueryClient, patientId: string, encounterId: string): Promise<void> {
  const key = claimKey(patientId, encounterId);
  if (!claim(qc, key)) return;
  let warning: string | null = null;
  try {
    const res = await advanceCareModels({ patientId, encounterId });
    // Whatever moved server-side, let the care-plan read catch up.
    void qc.invalidateQueries({ queryKey: carePlansKey(patientId) });
    // The readout's current step comes from C4; the advance just moved it.
    void qc.invalidateQueries({ queryKey: patientEnrolmentsKey(patientId) });
    const failed = res?.data?.failed;
    const advanced = res?.data?.advanced;
    if (Array.isArray(failed) && failed.length > 0)
      warning = Array.isArray(advanced) && advanced.length > 0 ? ADVANCE_PARTIAL_MESSAGE : ADVANCE_FAILED_MESSAGE;
  } catch (err) {
    warning = err instanceof ApiError && err.status === 409 ? ADVANCE_BUSY_MESSAGE : ADVANCE_FAILED_MESSAGE;
  }
  if (warning === null) return;
  claimed.get(qc)?.delete(key);
  showWarning(warning, {
    duration: 20_000,
    action: { label: 'Try again', onClick: () => void send(qc, patientId, encounterId) },
  });
}

/** Called from a SUCCESSFUL CreateSignLock with the record that was signed. Returns nothing, on purpose. */
export function advanceCareModelsAfterSign(qc: QueryClient, signed: SignLockModel): void {
  if (!env.careModelAdvanceEnabled) return;
  const { subject: patientId, encounter: encounterId } = signed;
  // Sign-and-lock only. Checked BEFORE the guard is claimed, so a nurse sign leaves it untouched.
  if (signed.sign !== true || signed.lock !== true || !patientId || !encounterId) return;
  void send(qc, patientId, encounterId);
}
